import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { ColorSelector } from "@/components/doc/right-sidebar/active-node-style/ColorSelector";
import { ConstantToggler } from "@/components/doc/right-sidebar/active-node-style/fields/ConstantToggler";
import { NodeFieldsBox } from "@/components/doc/right-sidebar/active-node-style/fields/NodeFieldsBox";
import { NodeNameField } from "@/components/doc/right-sidebar/active-node-style/fields/NodeNameField";
import { NodeFieldLabel } from "@/components/doc/right-sidebar/active-node-style/fields/NodeTextField";
import { ParametersField } from "@/components/doc/right-sidebar/active-node-style/fields/ParametersField";
import { UnitField } from "@/components/doc/right-sidebar/active-node-style/fields/UnitField";
import { BracketsSelector } from "@/components/doc/right-sidebar/active-node-style/group/BracketsSelector";
import { SymbolIndexFields } from "@/components/doc/right-sidebar/active-node-style/symbol/SymbolIndexFields";
import { SymbolValueField } from "@/components/doc/right-sidebar/active-node-style/symbol/SymbolValueField";
import {
  ACCENT_DECORATORS,
  CANCEL_DECORATORS,
  DOT_DECORATORS,
} from "@/components/doc/right-sidebar/active-node-style/variable/decorators";
import { DecoratorsMenu } from "@/components/doc/right-sidebar/active-node-style/variable/DecoratorsMenu";
import { DecoratorToggleButton } from "@/components/doc/right-sidebar/active-node-style/variable/DecoratorToggleButton";
import { SplitArguments } from "@/components/doc/right-sidebar/active-node-style/variable/SplitArguments";
import { useNodeTheme } from "@/components/expression/theme";
import { Value } from "@/components/expression/utils/layout";
import { NodeStyleProvider } from "@/components/expression/utils/NodeStyleProvider";
import { Sidebar } from "@/components/sidebar";
import { SidebarToolbar } from "@/components/sidebar/SidebarToolbar";
import { getDerivedUnitText } from "@/components/suggestions/providers/utils/getPhantomHints";

import type { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

type Props = {
  node: VariableNodeModel;
};

const DerivedUnitField = observer(function DerivedUnitField({
  node,
  label,
}: {
  node: VariableNodeModel;
  label: string;
}) {
  const derivedUnitText = getDerivedUnitText(node.doc, node.labeledValue);

  return (
    <NodeStyleProvider text="unit" view="text">
      <NodeFieldLabel
        as="div"
        label={label}
        className="py-0.5 pr-0.5 min-h-[28px] flex-1 min-w-0 flex-wrap shadow-none dark:shadow-none pointer-events-none"
        enableFocus={false}
        labelClassName="py-1 pr-1"
      >
        <div className="px-1 py-[1px] font-mono overflow-auto no-scrollbar flex items-baseline">
          <Value isReadable className="font-mono">
            {derivedUnitText}
          </Value>
        </div>
      </NodeFieldLabel>
    </NodeStyleProvider>
  );
});

export const VariableStyle = observer(function VariableStyle({ node }: Props) {
  const { doc, symbolNode } = node;
  const theme = useNodeTheme(node);
  const t = useTranslations("sidebar.activeNode");
  const tRoot = useTranslations();
  const hasDerivedUnit = !node.unitNode && getDerivedUnitText(doc, node.labeledValue) !== null;
  const hasColor = theme?.background.type === "colored";

  return (
    <div data-panel="variable_style">
      <div className="px-[5px]">
        <NodeFieldsBox>
          {symbolNode && (
            <SymbolValueField key={node.id} node={node} className="shadow-none dark:shadow-none" />
          )}
          {symbolNode && (
            <SymbolIndexFields
              key={`index-${node.id}`}
              node={symbolNode}
              contextId="label-field"
              className="shadow-none dark:shadow-none"
              withFieldDropdown
            />
          )}
          <NodeNameField node={node} className="shadow-none dark:shadow-none" />
          {node.unitNode && <UnitField node={node} className="shadow-none dark:shadow-none" />}
          {hasDerivedUnit && <DerivedUnitField node={node} label={t("fields.unit")} />}
          <ParametersField
            node={node}
            className="shadow-none dark:shadow-none"
            labelPlacement="inside"
            action={node.hasParams ? <SplitArguments node={node} className="w-6 h-6" /> : undefined}
          />
        </NodeFieldsBox>
        <SidebarToolbar
          label={tRoot("sidebar.toolbars.decorators")}
          className="items-center gap-1 my-1"
        >
          <DecoratorsMenu
            node={node}
            decoratorTypes={ACCENT_DECORATORS}
            tooltipTitle={tRoot("suggestions.decorator.applyAccent")}
          />
          <DecoratorsMenu
            node={node}
            decoratorTypes={DOT_DECORATORS}
            tooltipTitle={tRoot("suggestions.decorator.applyDot")}
          />
          <DecoratorsMenu
            node={node}
            decoratorTypes={CANCEL_DECORATORS}
            tooltipTitle={tRoot("suggestions.decorator.applyCancel")}
          />
          <DecoratorToggleButton node={node} decoratorType="boxed" />
          {hasColor && <ColorSelector node={node} className="ml-auto" />}
        </SidebarToolbar>
      </div>
      {node.hasParams && (
        <>
          <Sidebar.Delimiter className="my-1.25" />
          <BracketsSelector key={node.id} node={node} displayLimit={5} />
        </>
      )}
      {!node.hasParams && (
        <>
          <Sidebar.Delimiter className="my-1.25" />
          <ConstantToggler node={node} />
        </>
      )}
    </div>
  );
});
